/**
 * HomePage — Simon Cat Agent ｜萤火秘境
 *
 * 设计理念：让 UI 退后，让秘境成为主角
 *
 * 产品结构完整保留：
 * - 原有 6 大功能（发布调度 / AI Agent / 数据分析 / 媒体库 / 设置 / 账单）
 * - 全部 24 个支持平台
 * - 原有 CTA（免费试用 / 进入控制台 / 创建账号）
 * - Footer 版权信息
 * - 只改视觉，不改业务
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FireflyCanvas from '../components/magic/FireflyCanvas';

/* ═══════════════════════════════════════════
   原始业务数据 — 完整保留，不删不改
   ═══════════════════════════════════════════ */

const features = [
  {
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    title: '发布调度',
    description: '跨 20+ 社交平台 AI 辅助发布与排期管理',
    path: '/launches',
  },
  {
    icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    title: 'AI Agent',
    description: '智能代理自动创作内容并互动受众',
    path: '/agents',
  },
  {
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    title: '数据分析',
    description: '全平台数据追踪、互动监控与洞察分析',
    path: '/analytics',
  },
  {
    icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
    title: '媒体库',
    description: '图片视频集中管理，AI 智能分类整理',
    path: '/media',
  },
  {
    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
    title: '设置',
    description: '账号管理、集成配置、平台连接',
    path: '/settings',
  },
  {
    icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    title: '账单',
    description: '订阅管理、发票查看、套餐升级',
    path: '/billing',
  },
];

const platforms = [
  'X', 'LinkedIn', 'Instagram', 'TikTok', 'YouTube', 'Facebook',
  'Discord', 'Telegram', 'Bluesky', 'Reddit', 'Pinterest', 'Threads',
  'Medium', 'Dev.to', 'Hashnode', 'Tumblr', 'Mastodon', 'Slack',
  'WordPress', 'VK', 'Twitch', 'Kick', 'Nostr', 'Lemmy',
];

/* ═══════════════════════════════════════════
   组件
   ═══════════════════════════════════════════ */

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--night)' }}>

      {/* ── Navigation ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 32px',
          background: scrolled ? 'rgba(5, 15, 30, 0.78)' : 'rgba(5, 15, 30, 0.35)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          borderBottom: scrolled
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(255, 255, 255, 0.04)',
          transition: 'background 300ms var(--ease), border-bottom 300ms var(--ease)',
        }}
      >
        <div className="flex items-center gap-2.5">
          <img src="/logo-cat-white.png" alt="Simon Cat Agent" style={{ width: 28, height: 28 }} />
          <span style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 400, letterSpacing: '0.02em', fontFamily: 'var(--font-sans)' }}>
            Simon Cat
          </span>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => navigate('/auth/login')}
            className="home-nav-btn home-nav-login"
            style={{
              height: 36, padding: '0 18px', borderRadius: 10,
              fontSize: 13, fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              transition: 'all 200ms var(--ease)',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
              e.currentTarget.style.color = 'var(--text-primary)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
            }}
          >
            登录
          </button>
          <button
            onClick={() => navigate('/auth')}
            className="home-nav-btn home-nav-cta"
            style={{
              height: 36, padding: '0 22px', borderRadius: 10,
              fontSize: 13, fontWeight: 600,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              color: '#ffffff',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              transition: 'all 200ms var(--ease)',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(99, 102, 241, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.25)';
            }}
          >
            免费开始
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative w-full" style={{ height: '100vh', minHeight: 640, overflow: 'hidden' }}>
        {/* 神鹿湖畔实拍背景 */}
        <div
          className="absolute inset-0"
          style={{ backgroundImage: 'url(/hero-deer-forest.jpg)', backgroundSize: 'cover', backgroundPosition: 'center center' }}
        />
        {/* 左侧文字安全蒙版 */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, rgba(4,13,30,0.42) 0%, rgba(4,13,30,0.24) 35%, rgba(4,13,30,0.08) 65%, transparent 100%)' }}
        />
        {/* 底部沉降 — 湖面自然进入夜色（Spec 23） */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, transparent 60%, rgba(6,18,38,0.40) 80%, var(--night-900) 100%)' }}
        />
        {/* 萤火虫 — Canvas 粒子系统（首页：极慢漂浮，不拦截点击） */}
        <FireflyCanvas scrollFade mouseInteraction={false} speed={0.3} density={1.0} />

        {/* 文字内容 — z-20 确保在 FireflyCanvas(z-15) 之上，按钮可点击 */}
        <div className="absolute z-20" style={{ left: '9%', top: '46%', transform: 'translateY(-50%)', maxWidth: 620 }}>
          <p
            className="font-sans"
            style={{ fontSize: 12, letterSpacing: '0.18em', color: 'var(--text-secondary)', marginBottom: 20, textTransform: 'uppercase' }}
          >
            SIMON CAT
          </p>
          <h1
            style={{
              fontFamily: 'var(--font-serif)', fontSize: 'clamp(36px, 4.5vw, 56px)',
              fontWeight: 400, lineHeight: 1.15, color: 'var(--text-primary)', marginBottom: 18,
            }}
          >
            西蒙的猫 • 萤火秘境
          </h1>
          <p
            className="font-sans"
            style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: 12, maxWidth: 520 }}
          >
            AI 驱动的社交媒体管理与聊天平台
          </p>
          <p
            className="font-sans"
            style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: 36, maxWidth: 480 }}
          >
            跨 20+ 社交平台发布排期、构建智能 AI Agent、统一管理所有内容创作，一个工作区搞定一切。
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/auth')}
              className="border-none cursor-pointer font-sans font-medium"
              style={{
                height: 48, padding: '0 28px', borderRadius: 'var(--radius-pill)',
                fontSize: 15, background: 'var(--accent)', color: 'var(--night-900)',
                boxShadow: '0 4px 24px rgba(232, 210, 138, 0.18)',
                transition: 'opacity 150ms var(--ease)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              免费试用
            </button>
            <button
              onClick={() => navigate('/ai-os')}
              className="border-none cursor-pointer font-sans"
              style={{
                height: 48, padding: '0 28px', borderRadius: 'var(--radius-pill)',
                fontSize: 15, background: 'rgba(255,255,255,0.06)',
                color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.15)',
                transition: 'border-color 150ms var(--ease)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
            >
              进入控制台
            </button>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="relative" style={{ padding: '100px 32px 80px', background: 'var(--night)' }}>
        <div className="mx-auto" style={{ maxWidth: 1100 }}>
          <h2
            className="text-center"
            style={{
              fontFamily: 'var(--font-serif)', fontSize: 'clamp(24px, 3vw, 32px)',
              fontWeight: 400, color: 'var(--text-primary)', marginBottom: 12,
            }}
          >
            一个工作区，搞定一切
          </h2>
          <p
            className="text-center font-sans"
            style={{ fontSize: 15, color: 'var(--text-muted)', marginBottom: 60, lineHeight: 1.7 }}
          >
            完整的社交媒体 AI 管理工具链
          </p>
          <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {features.map((f) => (
              <button
                key={f.path}
                onClick={() => navigate(f.path)}
                className="text-left border-none cursor-pointer transition-all duration-300 group"
                style={{
                  background: 'rgba(8,24,48,0.58)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 20,
                  padding: '32px 28px',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.borderColor = 'rgba(217,238,255,0.18)';
                  e.currentTarget.style.background = 'rgba(8,24,48,0.72)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.background = 'rgba(8,24,48,0.58)';
                }}
              >
                <div
                  className="mb-4 flex items-center justify-center"
                  style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: 'var(--accent-soft)',
                    color: 'var(--accent)',
                  }}
                >
                  <svg width={22} height={22} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <path strokeLinecap="round" strokeLinejoin="round" d={f.icon} />
                  </svg>
                </div>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 19, fontWeight: 400, color: 'var(--text-primary)', marginBottom: 8 }}>
                  {f.title}
                </h3>
                <p className="font-sans" style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-muted)', margin: 0 }}>
                  {f.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platforms ── */}
      <section className="relative" style={{ padding: '60px 32px 80px', background: '#081932' }}>
        <div className="mx-auto" style={{ maxWidth: 1100 }}>
          <h2
            className="text-center"
            style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(22px, 2.5vw, 28px)', fontWeight: 400, color: 'var(--text-primary)', marginBottom: 10 }}
          >
            支持的平台
          </h2>
          <p className="text-center font-sans" style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 48 }}>
            连接并发布到 20+ 社交媒体平台
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {platforms.map((p) => (
              <div
                key={p}
                className="cursor-default"
                style={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8,
                  padding: '8px 16px',
                  color: 'rgba(233,242,255,0.58)',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                  background: 'rgba(255,255,255,0.02)',
                  transition: 'color 200ms, border-color 200ms',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'rgba(233,242,255,0.95)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(233,242,255,0.58)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                }}
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative" style={{ padding: '80px 32px 100px', background: 'var(--night-900)' }}>
        <div className="mx-auto text-center" style={{ maxWidth: 600 }}>
          <h2
            style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(24px, 3vw, 32px)', fontWeight: 400, color: 'var(--text-primary)', marginBottom: 14 }}
          >
            准备好了吗？
          </h2>
          <p className="font-sans" style={{ fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 36 }}>
            让 AI Agent 替你管理每天的社交内容。
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="border-none cursor-pointer font-sans font-medium"
            style={{
              height: 48, padding: '0 32px', borderRadius: 'var(--radius-pill)',
              fontSize: 15, background: 'var(--accent)', color: 'var(--night-900)',
              boxShadow: '0 4px 24px rgba(232, 210, 138, 0.18)',
              transition: 'opacity 150ms var(--ease)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            创建账号
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer
        className="relative"
        style={{
          background: 'linear-gradient(to bottom, var(--night-900) 0%, var(--night-950) 100%)',
          padding: '48px 32px 40px',
          borderTop: '1px solid var(--border-subtle)',
        }}
      >
        <div className="mx-auto text-center" style={{ maxWidth: 600 }}>
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo-cat-white.png" alt="Simon Cat Agent" style={{ width: 20, height: 20, opacity: 0.5 }} />
            <span className="font-sans" style={{ fontSize: 13, color: 'var(--text-muted)' }}>Simon Cat Agent</span>
          </div>
          <p className="font-sans" style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, letterSpacing: '0.04em' }}>
            © 2026 Simon Cat Agent. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
